import {
  Agent,
  PaymentMethodType,
  ProcessingLenderStrategyType,
  ProcessingMode,
  StopCondition,
} from '@nemuru-eng/js-api-client';
import { ChooseStrategyType } from '@nemuru-eng/js-api-client/slim';

import { OrchestrationConfig } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OrchestrationConfig';
import {
  OrchestrationGroup,
  OrchestrationRow,
} from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OrchestrationRow';

const ORCHESTRABLE_PAYMENT_METHOD_TYPES = [
  PaymentMethodType.CONSUMER_LOAN_INSTALMENTS,
  PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY,
  PaymentMethodType.BNPL_INSTALMENTS,
  PaymentMethodType.BNPL_SPLIT_PAY,
  PaymentMethodType.AUTO_LOAN,
  PaymentMethodType.CREDIT_LINE,
  PaymentMethodType.B2B_LOAN,
  PaymentMethodType.CARD_ONBOARDING,
];

export class MerchantOrchestrationMap {
  /** Mirror of AgentProcessingConfig::defaultFor() in api. */
  static defaultConfigFor(paymentMethodType: PaymentMethodType): OrchestrationConfig {
    if (paymentMethodType === PaymentMethodType.AUTO_LOAN) {
      return {
        processingStrategy: ProcessingLenderStrategyType.WATERFALL,
        processingMode: ProcessingMode.AUTOMATIC,
        stopCondition: StopCondition.ERROR,
        chooseStrategyType: ChooseStrategyType.MAX_PROFITABILITY,
        chooseStrategyTypeOptions: null,
      };
    }

    return {
      processingStrategy: ProcessingLenderStrategyType.PARALLEL,
      processingMode: ProcessingMode.AUTOMATIC,
      stopCondition: StopCondition.MAYBE,
      chooseStrategyType: ChooseStrategyType.MIN_APR,
      chooseStrategyTypeOptions: null,
    };
  }

  /** Equality key of a config. Mode and stop condition do not apply in parallel, so they are ignored there. */
  static configKey(config: OrchestrationConfig): string {
    const isParallel = config.processingStrategy === ProcessingLenderStrategyType.PARALLEL;
    const options = config.chooseStrategyTypeOptions
      ? Object.fromEntries(Object.entries(config.chooseStrategyTypeOptions).sort(([a], [b]) => a.localeCompare(b)))
      : null;

    return JSON.stringify([
      config.processingStrategy,
      isParallel ? '-' : config.processingMode,
      isParallel ? '-' : config.stopCondition,
      config.chooseStrategyType,
      options && Object.keys(options).length ? options : null,
    ]);
  }

  static isDefault(paymentMethodType: PaymentMethodType, config: OrchestrationConfig): boolean {
    return this.configKey(config) === this.configKey(this.defaultConfigFor(paymentMethodType));
  }

  static toRows(agents: Agent[]): OrchestrationRow[] {
    return ORCHESTRABLE_PAYMENT_METHOD_TYPES.flatMap((paymentMethodType) => {
      const groupsByKey = new Map<string, OrchestrationGroup>();

      agents.forEach((agent) => {
        const processingConfig = agent.processingConfigs.find(
          (config) => config.paymentMethodType === paymentMethodType,
        );
        if (!processingConfig) {
          return;
        }

        const config: OrchestrationConfig = {
          processingStrategy: processingConfig.processingStrategy,
          processingMode: processingConfig.processingMode,
          stopCondition: processingConfig.stopCondition,
          chooseStrategyType: processingConfig.chooseStrategyType,
          chooseStrategyTypeOptions: processingConfig.chooseStrategyTypeOptions,
        };
        const key = this.configKey(config);
        const group = groupsByKey.get(key) ?? {
          config,
          agents: [],
          isDefault: this.isDefault(paymentMethodType, config),
        };
        group.agents.push({ id: agent.id, name: agent.name });
        groupsByKey.set(key, group);
      });

      if (!groupsByKey.size) {
        return [];
      }

      const groups = [...groupsByKey.values()].sort(
        (a, b) =>
          b.agents.length - a.agents.length ||
          Number(b.config.processingStrategy === ProcessingLenderStrategyType.WATERFALL) -
            Number(a.config.processingStrategy === ProcessingLenderStrategyType.WATERFALL),
      );

      return [{ paymentMethodType, totalAgents: agents.length, groups, isMixed: groups.length > 1 }];
    });
  }

  static majorityConfig(row: OrchestrationRow): OrchestrationConfig {
    return row.groups[0].config;
  }
}
